import React from 'react'
import axios from 'axios'
import _ from 'lodash'
import { Dimmer, Loader } from 'semantic-ui-react'
import { Redirect } from 'react-router-dom'
import { BlueMoon, Dimple, handleError } from '../BlueMoon'
import { getTheme } from '../../config/clientConfig'
import * as M from './pacingMath'
import Picker from '../Common/Picker/Picker'
import './pacing.css'

/**
 * Partnership Pacing: current season to date vs the prior season over the same window
 * (KingsPacingDashboardV2-Lux), season totals. One fetch per season; months, filters and metric are client-side.
 */
class PacingDashboard extends React.Component {
    constructor(props) {
        super(props);
        this.theme = getTheme();
        this.state = {
            data: null, season: undefined,
            partners: [], channels: [], locations: [], months: [],
            metric: 'mev', sort: null,
            dimmer: true, error: null
        };
    }

    componentDidMount = () => {
        axios.defaults.headers.common = { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        this.fetchData();
    }

    // season undefined => backend default (latest season). Ticks missing from the new season are dropped; months persist.
    fetchData = (season) => {
        this.setState({ dimmer: true, error: null, season }, () => {
            axios.get(`${BlueMoon.ip}dashboard/pacing/`, {
                params: season ? { season } : {}
            }).then((response) => {
                const data = response.data;
                const options = M.optionLists(data.rows);
                this.setState((state) => ({
                    data, season: data.season, dimmer: false,
                    partners: state.partners.filter(p => options.partners.includes(p)),
                    channels: state.channels.filter(c => options.channels.includes(c)),
                    locations: state.locations.filter(l => options.locations.includes(l))
                }));
            }).catch((err) => {
                handleError(err);
                this.setState((state) => ({
                    dimmer: false,
                    error: 'Pacing data unavailable',
                    season: state.data ? state.data.season : undefined
                }));
            });
        });
    }

    // Multi pickers read 'All' when nothing is ticked (empty selection = no filter).
    picker = (label, multi, selected, options, onChange) => (
        <div className="f">
            <Picker label={label} multi={multi} selected={selected} options={options} onChange={onChange}
                emptyText={multi ? 'All' : undefined} />
        </div>
    )

    pick = (field) => (values) => this.setState({ [field]: values })

    // seasons missing => older backend; offer only the season we have
    seasonPicker = (data) => this.picker('Season', false, [this.state.season],
        (data.seasons || [data.season]).map(s => ({ value: s, text: M.longSeason(s) })), values => this.fetchData(values[0]))

    crest = (data) => (
        <div className="crest">
            <div className="brand">{this.theme.clientName} <span>· Partnership Pacing</span></div>
            {data ? (
                <div className="season">{M.longSeason(data.season)} <b>{data.seasonFinished ? 'Season · final' : 'Season to date'}</b><br />
                    {data.priorSeason ? `vs ${M.longSeason(data.priorSeason)}${data.seasonFinished ? '' : ' · same window'}` : 'no prior season'}
                </div>
            ) : null}
        </div>
    )

    meta = (data, games, curLabel, priorLabel) => {
        const { current, seasonLength } = data;
        const pct = current.games / seasonLength * 100;
        const vs = priorLabel ? ` vs ${priorLabel}` : '';
        return (
            <div className="meta">
                <div>
                    <div className="metaL">
                        <div className="bigpct">{pct.toFixed(0)}%</div>
                        <div>
                            <div className="metasub">{current.games} of {seasonLength} games</div>
                            <div className="track"><i style={{ width: `${pct}%` }} /></div>
                        </div>
                    </div>
                </div>
                <div className="gset">
                    {[['Local', 'local'], ['National', 'national']].map(([label, k]) => (
                        <div className="g" key={k}>
                            <div className="n">{games.current[k]}{priorLabel ? <span> vs {games.prior[k]}</span> : null}</div>
                            <div className="l">{label} · {curLabel}{vs}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    hero = (monthRows) => (
        <div className="hero">
            {M.kpis(monthRows).map(k => (
                <div className="kpi" key={k.key}>
                    <div className="l">{k.label}</div>
                    <div className="v">{M.formatMetric(k.key, k.total)}</div>
                    <div className="d">
                        <span className="pct" style={{ color: k.color }}>{M.formatDelta(k.deltaPct)}</span>
                    </div>
                </div>
            ))}
        </div>
    )

    controls = (data) => {
        const { partners, channels, locations, months, metric } = this.state;
        const options = M.optionLists(data.rows);
        const list = values => values.map(v => ({ value: v, text: v }));
        return (
            <div className="bar">
                <div className="fl">
                    {this.seasonPicker(data)}
                    {this.picker('Months', true, months, M.MONTHS.map(([value, text]) => ({ value, text })), this.pick('months'))}
                    {this.picker('Partner', true, partners, list(options.partners), this.pick('partners'))}
                    {this.picker('Channel', true, channels, list(options.channels), this.pick('channels'))}
                    {this.picker('Location', true, locations, list(options.locations), this.pick('locations'))}
                    {this.picker('Metric', false, [metric], _.map(M.METRICS, (m, key) => ({ value: key, text: m.label })),
                        values => this.setState({ metric: values[0] }))}
                </div>
            </div>
        );
    }

    chart = (data, rows, monthRows) => {
        const { metric } = this.state;
        const { series, max } = M.monthlySeries(rows, metric, M.chartMonths(monthRows));
        const height = v => `${v / max * 100}%`;
        const priorLabel = data.priorSeason ? M.longSeason(data.priorSeason) : null;
        return (
            <div className="chart">
                <div className="chartHead">
                    <div className="h2">Monthly · {M.METRICS[metric].label}</div>
                    <div className="lg">
                        {priorLabel ? <span><span className="dot pw" />{priorLabel}</span> : null}
                        <span><span className="dot cur" />{M.longSeason(data.season)}</span>
                    </div>
                </div>
                <div className="mchart">
                    {series.map(s => (
                        <div className="mcol" key={s.month}>
                            <div className="mbars">
                                <div className="mbar pw" title={M.formatMetric(metric, s.p)} style={{ height: height(s.p) }} />
                                <div className="mbar cur" title={M.formatMetric(metric, s.c)} style={{ height: height(s.c) }} />
                            </div>
                            <div className="mlab">{s.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Click cycles a column: ascending -> descending -> default order; another column starts ascending.
    onSort = (column) => this.setState(({ sort }) => ({
        sort: !sort || sort.column !== column ? { column, dir: 'asc' } : sort.dir === 'asc' ? { column, dir: 'desc' } : null
    }))

    sortHeader = (column, label) => {
        const { sort } = this.state;
        const dir = sort && sort.column === column ? sort.dir : null;
        const next = dir === 'asc' ? 'descending' : dir === 'desc' ? 'unsorted' : 'ascending';
        return (
            <th key={column} aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}>
                <button type="button" aria-label={`Sort ${label} ${next}`} onClick={() => this.onSort(column)}>
                    {label}{dir === 'asc' ? ' ↑' : dir === 'desc' ? ' ↓' : ''}
                </button>
            </th>
        );
    }

    table = (data, rows, notActive, curLabel, priorLabel) => {
        const { partners, metric, sort } = this.state;
        const list = M.sortTableRows(M.tableRows(rows, metric, partners, !!data.seasonFinished, notActive), sort);
        return (
            <table>
                <thead><tr>
                    {[['key', _.capitalize(M.groupField(partners))], ['pace', 'Pace'], ['c', curLabel],
                        ['p', priorLabel || 'Prior'], ['delta', 'Δ'], ['status', 'Status']]
                        .map(([column, label]) => this.sortHeader(column, label))}
                </tr></thead>
                <tbody>
                    {list.length ? list.map(r => {
                        // Not Active has no pace: empty rail (the fixed no-pace bar points right and would read as "ahead")
                        const rail = r.status.key === 'gone' ? { left: 50, width: 0 } : M.railGeometry(r.pace);
                        const color = M.STATUS_COLOR[r.status.key];
                        return (
                            <tr key={r.key}>
                                <td>{r.key}</td>
                                <td><div className="rail"><div className="mid" /><div className="bar2" style={{ left: `${rail.left}%`, width: `${rail.width}%`, background: color }} /></div></td>
                                <td className="num">{M.formatMetric(metric, r.c)}</td>
                                <td className="num prior">{priorLabel ? M.formatMetric(metric, r.p) : M.DASH}</td>
                                <td className="pace" style={{ color }}>{notActive.has(r.key) ? M.DASH : M.formatPace(r.pace)}</td>
                                <td><div className="stat" style={{ color }}><span className="dot" style={{ background: color }} />{r.status.label}</div></td>
                            </tr>
                        );
                    }) : (
                        <tr><td colSpan={6} className="empty">No delivery for this selection</td></tr>
                    )}
                </tbody>
            </table>
        );
    }

    content = (data) => {
        const { partners, channels, locations, months } = this.state;
        if (!data.rows.length) {
            return (
                <React.Fragment>
                    <div className="bar"><div className="fl">{this.seasonPicker(data)}</div></div>
                    <div className="empty">No pacing data for the {M.longSeason(data.season)} season</div>
                </React.Fragment>
            );
        }
        const curLabel = M.shortSeason(data.season);
        const priorLabel = data.priorSeason ? M.shortSeason(data.priorSeason) : null;
        // Months decide the whole page; partner/channel/location only the chart and table.
        const monthRows = M.filterRows(data.rows, { months });
        const rows = M.filterRows(data.rows, { partners, channels, locations, months });
        const games = M.gamesFor(data, months);
        // Not Active ignores the months filter (whole-season history)
        const notActive = M.notActiveKeys(data, { partners, channels, locations });
        return (
            <React.Fragment>
                {this.meta(data, games, curLabel, priorLabel)}
                {this.hero(monthRows)}
                {this.controls(data)}
                {this.chart(data, rows, monthRows)}
                {this.table(data, rows, notActive, curLabel, priorLabel)}
            </React.Fragment>
        );
    }

    render() {
        // same entry guards as Dashboard; tenants without the flag go to the main dashboard
        if (_.isEmpty(localStorage.getItem('user'))) window.location.replace(`${Dimple}#/login`);
        if (_.isEmpty(localStorage.getItem('projectIp'))) window.location.replace(`${Dimple}#/home`);
        if (_.isEmpty(localStorage.getItem('user'))) return null;
        if (!(this.theme.features && this.theme.features.pacing)) return <Redirect to="/dashboard/" />;

        const { data, dimmer, error } = this.state;
        return (
            <div className="pacing-page">
                <div className="pacing">
                    {this.crest(data)}
                    {error ? <div className="empty error">{error}</div> : null}
                    {data ? this.content(data) : null}
                    <Dimmer active={dimmer}><Loader /></Dimmer>
                </div>
            </div>
        );
    }
}

export default PacingDashboard;
